import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

const SECTIONS = ["Indoor", "Outdoor", "Private", "Bar", "Patio"];
const STATUS_COLORS = { available: "bg-emerald-100 text-emerald-700 border-emerald-200", occupied: "bg-red-100 text-red-700 border-red-200", reserved: "bg-amber-100 text-amber-700 border-amber-200" };

export default function TablesPage() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", capacity: "4", section: "Indoor" });

  const fetchTables = async () => {
    try {
      const res = await api.get("/tables");
      setTables(res.data);
    } catch { toast.error("Failed to load tables"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTables(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: `Table ${tables.length + 1}`, capacity: "4", section: "Indoor" });
    setShowDialog(true);
  };

  const openEdit = (table) => {
    setEditing(table);
    setForm({ name: table.name, capacity: String(table.capacity), section: table.section || "Indoor" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, capacity: parseInt(form.capacity) || 4 };
      if (editing) {
        await api.put(`/tables/${editing.table_id}`, payload);
        toast.success("Table updated");
      } else {
        await api.post("/tables", payload);
        toast.success("Table created");
      }
      setShowDialog(false);
      fetchTables();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this table?")) return;
    try { await api.delete(`/tables/${id}`); toast.success("Deleted"); fetchTables(); }
    catch { toast.error("Failed to delete"); }
  };

  const sections = [...new Set(tables.map((t) => t.section || "Other"))];

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-8" data-testid="tables-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="tables-title">Tables</h1>
          <p className="text-sm text-slate-500 mt-1">{tables.length} tables across {sections.length} sections</p>
        </div>
        <Button onClick={openCreate} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-table-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Table
        </Button>
      </div>

      {sections.map((section) => (
        <div key={section} className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-slate-700 mb-3">{section}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid={`tables-section-${section}`}>
            {tables.filter((t) => (t.section || "Other") === section).map((table) => (
              <div
                key={table.table_id}
                className={`p-4 rounded-lg border-2 text-center cursor-pointer hover:shadow-md transition-all ${STATUS_COLORS[table.status] || STATUS_COLORS.available}`}
                data-testid={`table-card-${table.table_id}`}
              >
                <p className="font-semibold text-sm mb-1">{table.name}</p>
                <div className="flex items-center justify-center gap-1 text-xs opacity-75">
                  <Users className="h-3 w-3" /> {table.capacity}
                </div>
                <Badge variant="outline" className="mt-2 text-xs capitalize">{table.status}</Badge>
                <div className="flex gap-1 justify-center mt-2">
                  <button onClick={() => openEdit(table)} className="p-1 rounded hover:bg-black/5" data-testid={`edit-table-${table.table_id}`}><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => handleDelete(table.table_id)} className="p-1 rounded hover:bg-black/5 text-red-500" data-testid={`delete-table-${table.table_id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="table-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Table" : "New Table"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">{editing ? "Update table details" : "Add a new table"}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Table Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="table-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Capacity</Label>
                <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="table-capacity-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Section</Label>
                <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                  <SelectTrigger data-testid="table-section-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="table-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="table-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
